// Spaced repetition for the travel phrase pages - a small Leitner system, kept
// in localStorage so it works with no account. Pure functions, so the round
// logic can be tested without rendering anything.
//
// A card is { at, box, due }. Box 0 is new or missed; each first-try right
// answer moves it up a box, and the box sets how long until it's due again.
// at is when it was last graded - it's how two devices' copies are merged.

export const DAY = 24 * 60 * 60 * 1000;
export const INTERVAL_DAYS = [0, 1, 3, 7, 16, 35];
export const ROUND_SIZE = 10;
export const CHOICES = 4;
export const REVIEW_PACK = "__review__";

export function isLearned(card) {
  return !!card && card.box >= 1;
}

export function isDue(card, now) {
  return isLearned(card) && card.due <= now;
}

export function grade(card, correct, now) {
  if (!correct) return { at: now, box: 0, due: now };

  const box = Math.min((card ? card.box : 0) + 1, INTERVAL_DAYS.length - 1);
  return { at: now, box, due: now + INTERVAL_DAYS[box] * DAY };
}

// Sections in page order, which is the phrasebook's order - Basics before
// Bars, the way a trip actually goes.
export function sectionsOf(phrases) {
  const seen = [];
  phrases.forEach((phrase) => {
    if (!seen.includes(phrase.section)) seen.push(phrase.section);
  });
  return seen;
}

// The phrasebook's own sections, in its own order, are the stops on the path.
// A stop is complete once every phrase in it has been got right at least once.
export function stopProgress(phrases, cards, section) {
  const inStop = phrases.filter((phrase) => phrase.section === section);
  const learned = inStop.filter((phrase) => isLearned(cards[phrase.id])).length;
  return { complete: inStop.length > 0 && learned === inStop.length, learned, total: inStop.length };
}

export function completedStops(phrases, cards) {
  return sectionsOf(phrases).filter((section) => stopProgress(phrases, cards, section).complete);
}

// The first unfinished stop, in path order.
export function nextStop(phrases, cards) {
  const sections = sectionsOf(phrases);
  return sections.find((section) => !stopProgress(phrases, cards, section).complete) || null;
}

// Two copies of the same progress - this browser's and the account's - merged
// card by card, the more recently graded copy winning. A card from before at
// existed counts as oldest.
export function mergeCards(mine, theirs) {
  const merged = { ...(mine || {}) };
  Object.entries(theirs || {}).forEach(([id, card]) => {
    const current = merged[id];
    if (!current || (card.at || 0) > (current.at || 0)) merged[id] = card;
  });
  return merged;
}

// Due reviews from the pack first, then new phrases in phrasebook order, then
// the pack's least-known learned phrases to fill a short pack out.
export function buildRound(phrases, cards, pack, now, size = ROUND_SIZE) {
  if (pack === REVIEW_PACK) {
    return phrases
      .filter((phrase) => isDue(cards[phrase.id], now))
      .sort((a, b) => cards[a.id].due - cards[b.id].due)
      .slice(0, size);
  }

  const inPack = phrases.filter((phrase) => phrase.section === pack);
  const due = inPack.filter((phrase) => isDue(cards[phrase.id], now));
  const fresh = inPack.filter((phrase) => !isLearned(cards[phrase.id]));
  const rest = inPack
    .filter((phrase) => isLearned(cards[phrase.id]) && !isDue(cards[phrase.id], now))
    .sort((a, b) => cards[a.id].box - cards[b.id].box);

  return [...due, ...fresh, ...rest].slice(0, size);
}

// What kind of question a phrase gets, by how well it's known:
//
//   new or missed  -> learn it first (a card showing everything), then
//                     "how do you say...?" - pick the phrase for the English
//   learned        -> "listen: what does it mean?" - hear it, pick the meaning,
//                     which is what understanding a reply on the street needs.
//                     "read" (the same, with the text instead) when there's no
//                     audio to play.
//   well known     -> alternating between the two, so neither skill fades.
export function quizKind(card, hasAudio) {
  const listen = hasAudio ? "listen" : "read";
  if (!isLearned(card)) return "produce";
  if (card.box >= 3) return card.box % 2 ? "produce" : listen;
  return listen;
}

// Kept for anything that only needs the old two-way split.
export function directionFor(card) {
  return isLearned(card) ? "recognize" : "produce";
}

export const NEW_PER_ROUND = 6;

// A round as a list of steps. Reviews come first as a warm-up; then new
// phrases two at a time - learn A, learn B, then quiz A and B - so each quiz
// is a moment's recall rather than a copy of the screen before.
export function buildSteps(phrases, cards, pack, now, hasAudio, size = ROUND_SIZE) {
  const round = buildRound(phrases, cards, pack, now, size + NEW_PER_ROUND);
  const fresh = round.filter((phrase) => !isLearned(cards[phrase.id])).slice(0, NEW_PER_ROUND);
  const reviews = round.filter((phrase) => isLearned(cards[phrase.id])).slice(0, Math.max(size - fresh.length, 0));
  const steps = reviews.map((phrase) => ({ ...phrase, kind: quizKind(cards[phrase.id], hasAudio) }));

  for (let i = 0; i < fresh.length; i += 2) {
    const pair = fresh.slice(i, i + 2);
    pair.forEach((phrase) => steps.push({ ...phrase, kind: "learn" }));
    pair.forEach((phrase) => steps.push({ ...phrase, kind: "produce" }));
  }

  return steps;
}

function shuffle(items, random) {
  const copy = items.slice();
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// Wrong answers from the same section where possible, near the right one in
// length so the answer can't be picked by shape alone, and never a duplicate
// of the right answer's text.
export function pickChoices(phrase, phrases, field, random = Math.random) {
  const answer = phrase[field];
  const used = new Set([answer]);
  const distractors = [];

  const take = (pool) => {
    const ranked = shuffle(pool, random)
      .filter((other) => other.id !== phrase.id && !used.has(other[field]))
      .sort((a, b) => Math.abs(a[field].length - answer.length) - Math.abs(b[field].length - answer.length))
      .slice(0, 6);

    shuffle(ranked, random).forEach((other) => {
      if (distractors.length < CHOICES - 1 && !used.has(other[field])) {
        used.add(other[field]);
        distractors.push(other);
      }
    });
  };

  take(phrases.filter((other) => other.section === phrase.section));
  if (distractors.length < CHOICES - 1) take(phrases);

  return shuffle([phrase, ...distractors], random);
}

export const STORAGE_PREFIX = "travel-phrases:";

// Wrapped because Safari private mode throws on access - losing progress is
// bad, a page that won't render is worse.
export function readStored(key, fallback) {
  try {
    const stored = window.localStorage.getItem(STORAGE_PREFIX + key);
    return stored === null ? fallback : JSON.parse(stored);
  } catch (e) {
    return fallback;
  }
}

export function writeStored(key, value) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch (e) {
    // Out of quota or blocked - the in-memory value still works for this visit.
  }
}
